import React, { useState, useRef, useEffect } from 'react';
import api from '../../services/api';

interface ConversationalCRMInterfaceProps {
  onContactUpdate?: () => void;
  onInteractionUpdate?: () => void;
}

interface AIResponse {
  success: boolean;
  data: {
    response: {
      type: 'action_result' | 'clarification' | 'confirmation';
      message: string;
      data?: any;
      intent?: {
        action: string;
        entity: string;
        confidence: number;
        extractedFields?: Record<string, any>;
      };
      suggestions?: string[];
    };
  };
}

export const ConversationalCRMInterface: React.FC<ConversationalCRMInterfaceProps> = ({
  onContactUpdate,
  onInteractionUpdate
}) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Array<{type: 'user' | 'assistant', content: string, timestamp: Date}>>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Process user message using AI
  const processMessage = async (userInput: string): Promise<AIResponse> => {
    try {
      const response = await api.post('/ai/crm/conversational', {
        message: userInput.trim(),
        context: {
          previousMessages: messages.slice(-5).map(m => ({
            role: m.type === 'user' ? 'user' : 'assistant',
            content: m.content
          }))
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error processing message:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isProcessing) return;

    const userMessage = input.trim();
    setInput('');
    setIsProcessing(true);

    // Add user message
    setMessages(prev => [...prev, { type: 'user', content: userMessage, timestamp: new Date() }]);

    try {
      // Process message using AI
      const result = await processMessage(userMessage);
      
      if (result.success) {
        const response = result.data.response;
        
        // For high-risk actions, show confirmation
        if (response.type === 'confirmation' && response.intent?.action?.includes('delete')) {
          setPendingMessage(userMessage);
          setShowConfirmation(true);
          setMessages(prev => [...prev, {
            type: 'assistant',
            content: `⚠️ Are you sure you want to ${response.intent?.action?.replace('_', ' ')}? This action cannot be undone.`,
            timestamp: new Date()
          }]);
          setIsProcessing(false);
          return;
        }

        // Add AI response
        setMessages(prev => [...prev, {
          type: 'assistant',
          content: response.message,
          timestamp: new Date()
        }]);

        // Trigger updates if action was successful
        if (response.type === 'action_result' && response.intent) {
          if (response.intent.entity === 'contact' && onContactUpdate) {
            onContactUpdate();
          }
          if (response.intent.entity === 'interaction' && onInteractionUpdate) {
            onInteractionUpdate();
          }
        }
      } else {
        setMessages(prev => [...prev, {
          type: 'assistant',
          content: '❌ Sorry, I encountered an error processing your request. Please try again.',
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Error processing message:', error);
      setMessages(prev => [...prev, {
        type: 'assistant',
        content: '❌ Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      }]);
    }

    setIsProcessing(false);
  };

  const handleConfirmation = async (confirmed: boolean) => {
    setShowConfirmation(false);
    
    if (confirmed && pendingMessage) {
      try {
        const result = await processMessage(pendingMessage);
        
        if (result.success) {
          setMessages(prev => [...prev, {
            type: 'assistant',
            content: result.data.response.message,
            timestamp: new Date()
          }]);

          // Trigger updates if action was successful
          if (result.data.response.type === 'action_result' && result.data.response.intent) {
            if (result.data.response.intent.entity === 'contact' && onContactUpdate) {
              onContactUpdate();
            }
            if (result.data.response.intent.entity === 'interaction' && onInteractionUpdate) {
              onInteractionUpdate();
            }
          }
        }
      } catch (error) {
        setMessages(prev => [...prev, {
          type: 'assistant',
          content: '❌ Sorry, I encountered an error. Please try again.',
          timestamp: new Date()
        }]);
      }
    } else if (pendingMessage) {
      setMessages(prev => [...prev, {
        type: 'assistant',
        content: `✅ Action cancelled.`,
        timestamp: new Date()
      }]);
    }
    
    setPendingMessage(null);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 h-96 flex flex-col">
      <div className="flex items-center mb-4">
        <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
        <h3 className="text-lg font-semibold text-gray-800">CRM Assistant</h3>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto mb-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-gray-500 text-sm">
            💬 Try saying: &quot;Add contact John Smith, email john@example.com, title Sales Manager&quot;
          </div>
        )}
        
        {messages.map((message, index) => (
          <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
              message.type === 'user' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              <div className="whitespace-pre-wrap">{message.content}</div>
              <div className={`text-xs mt-1 ${
                message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
              }`}>
                {message.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
        
        {isProcessing && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-800 px-4 py-2 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                <span>Processing...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input Form */}
      <form onSubmit={handleSubmit} className="flex space-x-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Tell me what you want to do with your CRM..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={isProcessing}
        />
        <button
          type="submit"
          disabled={isProcessing || !input.trim()}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Send
        </button>
      </form>
      
      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Action</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to proceed with this action? This cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => handleConfirmation(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleConfirmation(true)}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
